import { useState } from "react";

import {
  ColumnSortEnum,
  TableStateWithFilters,
} from "@king-ict/eupisi-ui/components";

import { TableUtils } from "../utils";

interface IProps<TFilter> {
  storageKey: string;
  defaultSortBy: number;
  defaultOrder?: ColumnSortEnum;
  defaultPageSize: number;
  extractFilters?: (state: TableStateWithFilters<TFilter>) => TFilter | undefined;
  defaultFilters: TFilter;
}

interface UseTableStateReturn<TFilter> {
  initialTableState: TableStateWithFilters<TFilter>;
  initialStoredFilters: TFilter;
  saveState: (
    filters: TFilter,
    tableState: TableStateWithFilters<TFilter>
  ) => void;
}

/**
 * Hook for managing table state with session storage persistence.
 * Handles initial state creation, filter extraction, and state saving.
 *
 * @template TFilter - The type of filter/search data stored in session storage
 * @param options - Configuration options for the hook
 * @returns Initial table state, extracted filters, and save function
 */
export function useTableState<TFilter>({
  storageKey,
  defaultSortBy,
  defaultOrder = ColumnSortEnum.ASC,
  defaultPageSize,
  extractFilters,
  defaultFilters,
}: IProps<TFilter>): UseTableStateReturn<TFilter> {
  const [initialTableState] = useState<TableStateWithFilters<TFilter>>(() => {
    return TableUtils.CreateInitialTableState<TFilter>(
      storageKey,
      TableUtils.tableDefaultState(defaultSortBy, defaultOrder, defaultPageSize)
    );
  });

  const initialStoredFilters = extractFilters
    ? extractFilters(initialTableState)
    : (initialTableState as TFilter);

  const saveState = (
    filters: TFilter,
    tableState: TableStateWithFilters<TFilter>
  ) => {
    TableUtils.SetSessionStorageItem<TFilter>(storageKey, filters, tableState);
  };

  return {
    initialTableState,
    initialStoredFilters: initialStoredFilters ?? defaultFilters,
    saveState,
  };
}
